import type { PropsWithChildren } from 'react'
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'

export type PseudolocMode = 'off' | 'full' | 'length'

const PSEUDOLOC_MODES: ReadonlyArray<PseudolocMode> = ['off', 'full', 'length'] as const

interface PseudolocContextValue {
  mode: PseudolocMode
  /** Whether pseudolocalization is enabled (full or length mode) */
  enabled: boolean
  /** Cycle through modes: off → full → length → off */
  toggle: () => void
  /** Set a specific mode directly */
  setMode: (mode: PseudolocMode) => void
}

const PseudolocContext = createContext<PseudolocContextValue | undefined>(undefined)

const STORAGE_KEY = 'i18n:pseudoloc:mode'

function isValidMode(value: string | null): value is PseudolocMode {
  return value === 'off' || value === 'full' || value === 'length'
}

export function PseudolocProvider({ children }: PropsWithChildren) {
  const [mode, setModeState] = useState<PseudolocMode>(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY)
      if (isValidMode(stored)) return stored
      // Migrate from old boolean storage key
      const legacy = sessionStorage.getItem('i18n:pseudoloc:enabled')
      if (legacy === 'true') return 'full'
      return 'off'
    } catch {
      return 'off'
    }
  })

  const setMode = useCallback((newMode: PseudolocMode) => {
    setModeState(newMode)
    try {
      sessionStorage.setItem(STORAGE_KEY, newMode)
    } catch {}
  }, [])

  const toggle = useCallback(() => {
    setModeState((prev) => {
      const currentIndex = PSEUDOLOC_MODES.indexOf(prev)
      const nextIndex = (currentIndex + 1) % PSEUDOLOC_MODES.length
      const newMode = PSEUDOLOC_MODES[nextIndex]
      try {
        sessionStorage.setItem(STORAGE_KEY, newMode)
      } catch {}
      return newMode
    })
  }, [])

  const enabled = mode !== 'off'

  // Keyboard shortcut: Cmd+Alt+Shift+P (Mac) or Ctrl+Alt+Shift+P (Windows/Linux)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for cmd+alt+shift+p (Mac) or ctrl+alt+shift+p (Windows/Linux)
      if (
        (event.metaKey || event.ctrlKey) &&
        event.altKey &&
        event.shiftKey &&
        event.code === 'KeyP'
      ) {
        event.preventDefault()
        toggle()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [toggle])

  return (
    <PseudolocContext.Provider value={{ mode, enabled, toggle, setMode }}>
      {children}
    </PseudolocContext.Provider>
  )
}

export function usePseudoloc(): PseudolocContextValue {
  const context = useContext(PseudolocContext)
  // Return default disabled state if context is not available (e.g., SSR)
  if (context === undefined) {
    return { mode: 'off', enabled: false, toggle: () => {}, setMode: () => {} }
  }
  return context
}
